import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './AdminPanel.css';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [centros, setCentros] = useState([]);
  const [tab, setTab] = useState('centros');
  const [loading, setLoading] = useState(true);
  const [selectedCentro, setSelectedCentro] = useState(null);
  const [plantas, setPlantas] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [panoramas, setPanoramas] = useState([]);
  const [editingCentro, setEditingCentro] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    lat: '',
    lng: '',
    ciudad: '',
    pais: 'España',
    direccion: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      console.log('❌ No hay token, redirigiendo a login');
      navigate('/admin');
      return;
    }
    console.log('✅ Token encontrado, cargando centros...');
    cargarCentros();
  }, [navigate]);

  const cargarCentros = async () => {
    try {
      const res = await api.get('/api/centros');
      if (res.data.success) setCentros(res.data.centros || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos completos del centro para edición (incluyendo direccion si existe)
  const cargarCentroParaEdicion = async (centroId) => {
    try {
      const res = await api.get(`/api/centros/${centroId}`);
      if (res.data.success && res.data.centro) {
        const centro = res.data.centro;
        setFormData({
          nombre: centro.nombre || '',
          descripcion: centro.descripcion || '',
          lat: centro.lat || '',
          lng: centro.lng || '',
          ciudad: centro.ciudad || '',
          pais: centro.pais || 'España',
          direccion: centro.direccion || ''
        });
      }
    } catch (err) {
      console.error('Error al cargar datos del centro:', err);
    }
  };

  const cargarCentroCompleto = async (id) => {
    setSelectedCentro(id);
    try {
      const res = await api.get(`/api/centros/${id}`);
      if (res.data.success) {
        setPlantas(res.data.centro.plantas || []);
        setTiendas(res.data.centro.tiendas || []);
        setPanoramas(res.data.centro.panoramas || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleAgregarCentro = async () => {
    const nombre = prompt('Nombre del centro comercial:');
    if (!nombre) return;
    try {
      const res = await api.post('/api/centros', {
        nombre: nombre.trim(),
        lat: 40.4,
        lng: -3.7,
        pais: 'España',
        ciudad: 'Madrid'
      });
      if (res.data.success) {
        cargarCentros();
        alert('Centro creado correctamente.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Error al crear';
      alert('Error al crear: ' + msg);
    }
  };

  const handleAgregarTienda = async () => {
    if (!selectedCentro) return alert('Selecciona un centro primero');
    const nombre = prompt('Nombre de la tienda:');
    if (!nombre) return;
    try {
      await api.post('/api/tiendas', {
        centro_id: selectedCentro,
        nombre,
        descripcion: prompt('Descripción (opcional):') || '',
        categoria: prompt('Categoría (Moda, Restauración, etc.):') || '',
        pitch: 0,
        yaw: 0
      });
      cargarCentroCompleto(selectedCentro);
      alert('Tienda creada. Configura posición (pitch/yaw) al editar desde el visor.');
    } catch (err) {
      alert(err.response?.data?.message || 'Error al crear');
    }
  };

  const handleEditarCentro = async (centro) => {
    setEditingCentro(centro.id);
    // Cargar datos completos del centro (por si falta direccion en la lista)
    await cargarCentroParaEdicion(centro.id);
    // También establecer datos básicos por si acaso
    setFormData({
      nombre: centro.nombre || '',
      descripcion: centro.descripcion || '',
      lat: centro.lat || '',
      lng: centro.lng || '',
      ciudad: centro.ciudad || '',
      pais: centro.pais || 'España',
      direccion: centro.direccion || ''
    });
  };

  const handleGuardarCentro = async () => {
    if (!editingCentro) return;
    
    if (!formData.lat || !formData.lng) {
      alert('Por favor ingresa las coordenadas (latitud y longitud)');
      return;
    }

    try {
      // Convertir formato europeo (coma) a formato estándar (punto) para números
      const convertirNumero = (valor) => {
        if (!valor || valor === '') return null;
        // Reemplazar coma por punto si es formato europeo
        const valorLimpio = String(valor).replace(',', '.');
        const num = parseFloat(valorLimpio);
        return isNaN(num) ? null : num;
      };

      // Función para asegurar que nunca sea undefined
      const safeValue = (valor, defaultValue = null) => {
        if (valor === undefined || valor === null || valor === '') {
          return defaultValue;
        }
        return valor;
      };

      // Preparar datos asegurándonos de que no haya undefined
      // Solo enviar los campos que realmente queremos actualizar
      const datos = {
        nombre: safeValue(formData.nombre, ''),
        descripcion: safeValue(formData.descripcion, null),
        lat: convertirNumero(formData.lat),
        lng: convertirNumero(formData.lng),
        ciudad: safeValue(formData.ciudad, null),
        pais: safeValue(formData.pais, 'España'),
        direccion: safeValue(formData.direccion, null)
        // No enviamos imagen_portada, activo, orden - el backend los mantendrá como están
      };

      // Validar que las coordenadas sean números válidos
      if (datos.lat === null || datos.lng === null) {
        alert('Las coordenadas deben ser números válidos');
        return;
      }

      // Verificar que no haya undefined antes de enviar
      const tieneUndefined = Object.values(datos).some(val => val === undefined);
      if (tieneUndefined) {
        console.error('❌ ERROR: Se detectaron valores undefined en los datos:', datos);
        alert('Error: Hay valores inválidos en el formulario. Por favor revisa los campos.');
        return;
      }

      console.log('📤 Enviando datos al backend:', datos);
      const response = await api.put(`/api/centros/${editingCentro}`, datos);
      console.log('✅ Respuesta del backend:', response.data);
      alert('Centro actualizado correctamente');
      setEditingCentro(null);
      cargarCentros();
    } catch (err) {
      console.error('❌ Error al guardar:', err);
      console.error('❌ Respuesta del error:', err.response?.data);
      console.error('❌ Stack:', err.stack);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Error al actualizar';
      alert(`Error: ${errorMsg}`);
    }
  };

  const handleCancelarEdicion = () => {
    setEditingCentro(null);
    setFormData({
      nombre: '',
      descripcion: '',
      lat: '',
      lng: '',
      ciudad: '',
      pais: 'España',
      direccion: ''
    });
  };

  const buscarCoordenadas = async () => {
    const direccion = prompt('Ingresa la dirección completa del centro comercial:');
    if (!direccion) return;
    
    try {
      // Usar Nominatim (OpenStreetMap) para geocodificación gratuita
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(direccion)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setFormData({
          ...formData,
          lat: parseFloat(data[0].lat).toFixed(7),
          lng: parseFloat(data[0].lon).toFixed(7),
          direccion: direccion,
          ciudad: data[0].address?.city || data[0].address?.town || data[0].address?.village || ''
        });
        alert(`Coordenadas encontradas: ${data[0].lat}, ${data[0].lon}`);
      } else {
        alert('No se encontraron coordenadas para esa dirección');
      }
    } catch (err) {
      alert('Error al buscar coordenadas. Puedes ingresarlas manualmente.');
    }
  };

  if (loading) return <div className="admin-loading">Cargando...</div>;

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>Panel de administración</h1>
        <div>
          <a href="/admin/constructor" className="btn-link">Constructor</a>
          <a href="/admin/generar-360" className="btn-link">Crear 360 con IA</a>
          <a href="/" className="btn-link">Ver Globe</a>
          <button onClick={handleLogout} className="btn-logout">Cerrar sesión</button>
        </div>
      </header>

      <div className="admin-tabs">
        <button className={tab === 'centros' ? 'active' : ''} onClick={() => setTab('centros')}>Centros</button>
        <button className={tab === 'tiendas' ? 'active' : ''} onClick={() => setTab('tiendas')}>Tiendas</button>
      </div>

      <div className="admin-content">
        {tab === 'centros' && (
          <section>
            <div className="section-header">
              <h2>Centros comerciales</h2>
              <button onClick={handleAgregarCentro} className="btn-primary">+ Nuevo centro</button>
            </div>
            <ul className="lista-centros">
              {centros.map(c => (
                <li key={c.id}>
                  {editingCentro === c.id ? (
                    <div className="form-edicion-centro">
                      <div className="form-group">
                        <label>Nombre:</label>
                        <input
                          type="text"
                          value={formData.nombre}
                          onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                          placeholder="Nombre del centro"
                        />
                      </div>
                      <div className="form-group">
                        <label>Descripción:</label>
                        <textarea
                          value={formData.descripcion}
                          onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                          placeholder="Descripción del centro"
                          rows="3"
                        />
                      </div>
                      <div className="form-group">
                        <label>Dirección:</label>
                        <div style={{display: 'flex', gap: '10px'}}>
                          <input
                            type="text"
                            value={formData.direccion}
                            onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                            placeholder="Dirección completa"
                            style={{flex: 1}}
                          />
                          <button type="button" onClick={buscarCoordenadas} className="btn-buscar">
                            🔍 Buscar coordenadas
                          </button>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Latitud:</label>
                          <input
                            type="text"
                            value={formData.lat}
                            onChange={(e) => {
                              // Permitir números con punto o coma como separador decimal
                              const valor = e.target.value.replace(/[^0-9.,-]/g, '');
                              setFormData({...formData, lat: valor});
                            }}
                            placeholder="40.4168"
                          />
                          <small style={{color: '#666', fontSize: '0.85rem'}}>Formato: 40.4168 o 40,4168</small>
                        </div>
                        <div className="form-group">
                          <label>Longitud:</label>
                          <input
                            type="text"
                            value={formData.lng}
                            onChange={(e) => {
                              // Permitir números con punto o coma como separador decimal
                              const valor = e.target.value.replace(/[^0-9.,-]/g, '');
                              setFormData({...formData, lng: valor});
                            }}
                            placeholder="-3.7038"
                          />
                          <small style={{color: '#666', fontSize: '0.85rem'}}>Formato: -3.7038 o -3,7038</small>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Ciudad:</label>
                          <input
                            type="text"
                            value={formData.ciudad}
                            onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                            placeholder="Madrid"
                          />
                        </div>
                        <div className="form-group">
                          <label>País:</label>
                          <input
                            type="text"
                            value={formData.pais}
                            onChange={(e) => setFormData({...formData, pais: e.target.value})}
                            placeholder="España"
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button onClick={handleGuardarCentro} className="btn-primary">Guardar</button>
                        <button onClick={handleCancelarEdicion} className="btn-secondary">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>{c.nombre}</strong>
                        {c.ciudad && <span className="ciudad">{c.ciudad}</span>}
                        {c.lat && c.lng && (
                          <span className="coordenadas">
                            📍 {parseFloat(c.lat).toFixed(4)}, {parseFloat(c.lng).toFixed(4)}
                          </span>
                        )}
                      </div>
                      <div className="acciones">
                        <button onClick={() => handleEditarCentro(c)} className="btn-editar">Editar</button>
                        <a href={`/centro/${c.id}`} target="_blank" rel="noopener noreferrer">Ver</a>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {tab === 'tiendas' && (
          <section>
            <div className="section-header">
              <h2>Tiendas</h2>
              <button onClick={handleAgregarTienda} className="btn-primary" disabled={!selectedCentro}>+ Nueva tienda</button>
            </div>
            <div className="selector-centro">
              <label>Centro: </label>
              <select value={selectedCentro || ''} onChange={e => cargarCentroCompleto(parseInt(e.target.value))}>
                <option value="">Seleccionar...</option>
                {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            {selectedCentro && (
              <>
                <p className="info">Panoramas: {panoramas.length} | Plantas: {plantas.length}</p>
                <ul className="lista-tiendas">
                  {tiendas.map(t => (
                    <li key={t.id}>
                      <strong>{t.nombre}</strong>
                      {t.categoria && <span className="badge">{t.categoria}</span>}
                      {t.planta_nombre && <span>{t.planta_nombre}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
